import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

const getErrorMessage = (error, fallbackMessage) =>
  error.response?.data?.message || error.response?.data?.errors?.[0]?.message || fallbackMessage;

const getOrderItemProductId = (item) => {
  if (!item?.product) {
    return null;
  }

  if (typeof item.product === 'object' && item.product._id) {
    return item.product._id;
  }

  return item.product;
};

// Fetch user's orders
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/orders', { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch orders'));
    }
  }
);

// Fetch single order
export const fetchOrder = createAsyncThunk(
  'orders/fetchOrder',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/orders/${id}`);
      return response.data.data.order;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Order not found'));
    }
  }
);

// Create order
export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await api.post('/orders', orderData);
      return response.data.data.order;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create order'));
    }
  }
);

// Cancel order
export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/orders/${id}/cancel`, { reason });
      return response.data.data.order;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to cancel order'));
    }
  }
);

// Add or update review for an item in an order
export const submitOrderItemReview = createAsyncThunk(
  'orders/submitOrderItemReview',
  async ({ orderId, productId, rating, title, comment }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/orders/${orderId}/review`, {
        productId,
        rating,
        title,
        comment
      });

      return {
        orderId,
        productId,
        review: response.data.data.review
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to submit review'));
    }
  }
);

// Admin: Fetch all orders
export const fetchAdminOrders = createAsyncThunk(
  'orders/fetchAdminOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/orders/admin/all', { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch orders'));
    }
  }
);

// Admin: Update order status
export const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/orders/${id}/status`, data);
      return response.data.data.order;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update order'));
    }
  }
);

// Admin: Bulk update order statuses
export const bulkUpdateOrderStatuses = createAsyncThunk(
  'orders/bulkUpdateOrderStatuses',
  async ({ orderIds, status, paymentStatus }, { rejectWithValue }) => {
    try {
      const response = await api.patch('/orders/admin/bulk-status', {
        orderIds,
        status,
        paymentStatus
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update selected orders'));
    }
  }
);

const initialState = {
  orders: [],
  order: null,
  pagination: {
    current: 1,
    pages: 1,
    total: 0,
  },
  isLoading: false,
  error: null,
};

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearOrder: (state) => {
      state.order = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Single Order
      .addCase(fetchOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        // Normalize order to ensure orderStatus is available
        state.order = {
          ...action.payload,
          orderStatus: action.payload.orderStatus || action.payload.status
        };
      })
      .addCase(fetchOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.order = action.payload;
        state.orders.unshift(action.payload);
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Cancel Order
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.order = action.payload;
        const index = state.orders.findIndex(o => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      // Submit Order Item Review
      .addCase(submitOrderItemReview.pending, (state) => {
        state.error = null;
      })
      .addCase(submitOrderItemReview.fulfilled, (state, action) => {
        if (!state.order?.items?.length) {
          return;
        }

        const { productId, review } = action.payload;

        state.order.items = state.order.items.map((item) => {
          if (String(getOrderItemProductId(item)) !== String(productId)) {
            return item;
          }

          return {
            ...item,
            userReview: review,
            canReview: true
          };
        });
      })
      .addCase(submitOrderItemReview.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Admin Orders
      .addCase(fetchAdminOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAdminOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        // Normalize orders to ensure orderStatus is available
        state.orders = action.payload.orders.map(order => ({
          ...order,
          orderStatus: order.orderStatus || order.status
        }));
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAdminOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Order Status
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.order = action.payload;
        const index = state.orders.findIndex(o => o._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      // Bulk Update Order Status
      .addCase(bulkUpdateOrderStatuses.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(bulkUpdateOrderStatuses.fulfilled, (state, action) => {
        state.isLoading = false;
        const { updatedOrderIds, status, paymentStatus } = action.payload;

        state.orders = state.orders.map((order) => {
          if (!updatedOrderIds.includes(order._id)) {
            return order;
          }

          const nextOrder = { ...order };
          if (status) {
            nextOrder.status = status;
            nextOrder.orderStatus = status;
          }
          if (paymentStatus) {
            nextOrder.paymentStatus = paymentStatus;
          }

          return nextOrder;
        });
      })
      .addCase(bulkUpdateOrderStatuses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearOrder } = orderSlice.actions;
export default orderSlice.reducer;
